import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { fetchConversations, type Conversation } from "@/lib/chat";
import ConversationList from "./components/ConversationList";
import ConversationView from "./components/ConversationView";

export default function MessagesPage() {
  const { user } = useAuth();
  const { conversationId } = useParams<{ conversationId: string }>();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchConversations(user.id);
      setConversations(data);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "No se pudieron cargar los mensajes.",
      );
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const active = conversationId
    ? conversations.find((c) => c.id === conversationId) ?? null
    : null;
  const hasSelection = Boolean(conversationId);

  return (
    <div className="min-h-screen bg-background-50">
      <main className="mx-auto max-w-7xl px-4 py-6 md:px-6 md:py-8">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100">
            <i className="ri-chat-3-line text-xl text-primary-500"></i>
          </span>
          <div>
            <h1 className="font-heading text-xl font-bold text-foreground-950">
              Mensajes
            </h1>
            <p className="text-sm text-foreground-600">
              Chateá con compradores y vendedores sobre tus publicaciones.
            </p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 lg:h-[calc(100vh-160px)] lg:grid-cols-[360px_1fr]">
          <div className={hasSelection ? "hidden lg:block" : "block"}>
            <ConversationList
              conversations={conversations}
              loading={loading}
              error={error}
              activeId={conversationId}
              onRetry={load}
            />
          </div>

          <div className={hasSelection ? "block" : "hidden lg:block"}>
            <ConversationView
              conversation={active}
              loading={loading}
              conversationId={conversationId}
              currentUserId={user?.id ?? ""}
              onMessageSent={load}
            />
          </div>
        </div>
      </main>
    </div>
  );
}